import { getFormProps, useForm } from '@conform-to/react'
import { startOfDay } from 'date-fns'
import * as React from 'react'
import { useState } from 'react'
import { useFetcher, Link, Form, useLoaderData } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button.tsx'
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from '#app/components/ui/command'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import { cn } from '#app/utils/misc.tsx'
import { backgroundColor } from '#app/utils/user-schedule.ts'
import { useOptionalAdminUser } from '#app/utils/user.ts'
import { type Route } from './+types/crossovers.ts'
import {
	type action,
	type loader,
	type UserScheduleType,
} from './crossovers.server.tsx'

export { loader, action } from './crossovers.server.tsx'

export default function CrossoversRoute() {
	const { users, currentUser, status, schedule, userSchedules } =
		useLoaderData<typeof loader>()

	const fetcher = useFetcher()
	const handleIssueCredits = () =>
		fetcher.submit({ intent: 'issueCredits' }, { method: 'POST' })

	const userIsAdmin = useOptionalAdminUser()
	const [showButtons, setShowButtons] = useState({
		admin: false,
		sorting: false,
	})
	const toggleSortingButtons = () =>
		setShowButtons((prev) => ({ ...prev, sorting: !prev.sorting }))
	const toggleAdminButtons = () =>
		setShowButtons((prev) => ({ ...prev, admin: !prev.admin }))
	if (!schedule || status !== 'idle') return null

	return (
		<div className="flex flex-col items-center justify-center gap-1 px-4 md:px-8">
			<h1 className="text-h1 text-center">
				Gate Changes & Crossovers | {schedule.date}
			</h1>
			<h2 className="text-h2">Acknowledgements and Volunteer Signup</h2>
			<h3 className="text-h3 text-center">{schedule.schedule.join(' ─ ')}</h3>
			{userIsAdmin ? (
				<div
					id="admin-row"
					className="flex w-full flex-row justify-end gap-1.5"
				>
					{schedule.credited ? (
						<Button>
							<Link reloadDocument to={`/schedule/${schedule.date}/credits`}>
								<Icon
									name="credits"
									className="mr-1 scale-125 max-md:scale-150"
								>
									<span className="max-md:hidden">View Credits</span>
								</Icon>
							</Link>
						</Button>
					) : (
						<Button className="hidden sm:flex" onClick={handleIssueCredits}>
							<Icon name="credits" className="mr-1 scale-125 max-md:scale-150">
								<span className="max-md:hidden">Issue Credits</span>
							</Icon>
						</Button>
					)}
					<Button
						variant="secondary"
						className="text-secondary-foreground w-48"
						onClick={toggleAdminButtons}
					>
						{`${showButtons.admin ? '▲ Hide' : '▼ Show'} Admin Buttons`}
					</Button>
					<Button
						className="hidden sm:flex"
						variant="secondary"
						onClick={toggleSortingButtons}
					>
						<Icon name={`${showButtons.sorting ? 'height' : 'row-spacing'}`} />
					</Button>
				</div>
			) : null}
			<div className="flex flex-col items-start gap-1">
				<h5 className="text-h5 mt-2 underline">
					Task Confirmation and Support Options:
				</h5>
				{referenceKey.map((key, index) => (
					<SampleButton key={`reference-key-${index}`} {...key} />
				))}
				<h6 className="text-h6 mb-2">
					Note: These buttons will be disabled 48-hours before their designated
					start time.
				</h6>
			</div>
			{userSchedules.map((userSchedule) => (
				<UserCard
					key={userSchedule.crossoverId}
					users={users}
					schedule={{ id: schedule.id, date: schedule.date }}
					currentUser={currentUser}
					userSchedule={userSchedule}
					showButtons={showButtons}
				/>
			))}
		</div>
	)

	function SampleButton({
		color,
		label,
		description,
		className,
	}: {
		color: string
		label: string
		description: string
		className?: string
	}) {
		return (
			<div id="key-row" className={cn('ml-2 flex flex-row gap-1', className)}>
				<div className="ml-1 flex flex-col justify-center whitespace-pre-wrap">
					<Button
						variant="outline"
						className={`pointer-events-none w-40 border-2 border-${color} text-primary hover:bg-background shadow-sm shadow-gray-700`}
					>
						{label}
					</Button>
				</div>
				<div className="ml-1 flex flex-col justify-center whitespace-pre-wrap">
					{description}
				</div>
			</div>
		)
	}
}

function UserCard({
	users,
	schedule,
	currentUser,
	userSchedule: {
		userId,
		ditch,
		position,
		crossoverId,
		order,
		display,
		quickbooks,
		duty,
		dutyStart,
		dutyStartString,
		dutyDetail,
		dutyNotes,
		startString,
		isCurrentSchedule,
		isCurrentUser,
		isCurrentVolunteer,
		isLocked,
		distanceToNow,
		acknowledged,
		volunteer,
		volunteerId,
		requestsTraining,
	},
	showButtons,
}: {
	users: { id: string; quickbooks: string }[]
	schedule: { id: string; date: string }
	currentUser: string | null
	userSchedule: UserScheduleType
	showButtons: { admin: boolean; sorting: boolean }
}) {
	const [form] = useForm({ id: crossoverId })
	const first = Boolean(duty === 'first')
	const fetcher = useFetcher<typeof action>()
	// Use fetcher.data to prevent loader refetch
	const currentDutyNotes = (fetcher.data?.dutyNotes ?? dutyNotes) as
		| string
		| null
		| undefined

	const handleChange = (dutyNotes: string) =>
		void fetcher.submit(
			{ intent: 'dutyNotes', crossoverId, dutyNotes },
			{ method: 'POST' },
		)

	const dutyStartDate = dutyStart ? new Date(dutyStart) : startOfDay(new Date())
	const getTimestamp = (ev: any): string => {
		if (!ev.target['validity'].valid) return ''
		return ev.target['value'] + ':00Z'
	}
	const handleChangeTimestamp = (ev: any) => {
		const timestamp = getTimestamp(ev)
		void fetcher.submit(
			{ intent: 'setTime', crossoverId, timestamp },
			{ method: 'POST' },
		)
	}
	const ditchPosition = `${ditch}.${String(position).padStart(2, '0')}`

	const CrossoverSortingButtons = ({
		crossoverId,
		order,
		start,
	}: {
		crossoverId: string
		order?: number
		start?: number
	}) => {
		const handleSort = (intent: string) =>
			fetcher.submit(
				{
					scheduleId: schedule.id,
					crossoverId,
					...(order !== undefined && { order }),
					...(start !== undefined && { start }),
					intent,
				},
				{ method: 'POST' },
			)

		return (
			<div>
				<Button
					type="button"
					onClick={() => handleSort('decrement')}
					variant="outline"
					disabled={order === 1}
					className="text-primary ml-1 rounded-b-none border-2 shadow-sm shadow-gray-700"
				>
					<Icon name="chevron-up" />
				</Button>
				<Button
					type="button"
					onClick={() => handleSort('increment')}
					variant="outline"
					disabled={order === 20}
					className="text-primary ml-1 rounded-t-none border-2 shadow-sm shadow-gray-700"
				>
					<Icon name="chevron-down" />
				</Button>
			</div>
		)
	}

	const DutiesDropdownMenu = ({
		crossoverId,
		display,
	}: {
		crossoverId: string
		display: string
	}) => {
		const handleSelect = (intent: string) =>
			fetcher.submit({ crossoverId, intent }, { method: 'POST' })

		return (
			<DropdownMenu>
				<DropdownMenuTrigger className="w-12">
					<Button variant="outline">
						<Icon name="ellipsis" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-56">
					<DropdownMenuLabel className="text-lg">{display}</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuGroup>
						<DropdownMenuItem
							className="text-green-700"
							onSelect={() => handleSelect('acknowledge')}
						>
							<Icon
								name="check-circled"
								className="mr-2 h-6 w-6"
								aria-hidden="true"
							/>
							Acknowledged
						</DropdownMenuItem>
						<DropdownMenuItem
							className="text-[#f3ab23]"
							onSelect={() => handleSelect('training')}
						>
							<Icon
								name="check-circled"
								className="mr-2 h-6 w-6"
								aria-hidden="true"
							/>
							Request Training
						</DropdownMenuItem>
						<DropdownMenuItem
							className="text-red-700"
							onSelect={() => handleSelect('assistance')}
						>
							<Icon
								name="cross-circled"
								className="mr-2 h-6 w-6"
								aria-hidden="true"
							/>
							Request Help
						</DropdownMenuItem>
						<DropdownMenuItem
							className="text-yellow-900"
							onSelect={() => handleSelect('clear')}
						>
							<Icon
								name="broom"
								className="mx-1.5 h-4 w-4"
								aria-hidden="true"
							/>
							<p className="ml-1">Clear Response</p>
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	const VolunteerDropdownMenu = ({
		users,
		crossoverId,
		scheduleDate,
		currentVolunteer,
	}: {
		crossoverId: string
		scheduleDate: string
		currentVolunteer?: string | null
		users: { id: string; quickbooks: string }[]
	}) => {
		const [open, setOpen] = React.useState(false)
		const [volunteerId, setVolunteerId] = React.useState(currentVolunteer ?? '')

		const userMap = new Map(users.map((user) => [user.id, user.quickbooks]))
		const quickbooksMap = new Map(
			users.map((user) => [user.quickbooks.toLowerCase(), user.id]),
		)

		const handleSelect = (currentValue: string) => {
			const volunteerId = quickbooksMap.get(currentValue.toLowerCase()) ?? ''
			setVolunteerId(volunteerId)
			void fetcher.submit(
				{ crossoverId, volunteerId, intent: 'volunteer' },
				{ method: 'POST' },
			)
			setOpen(false)
		}

		return (
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className="w-80 grow justify-between"
					>
						{userMap.get(volunteerId) ?? 'Select Volunteer'}
						<Icon name="chevrons-up-down" className="opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-80 p-0">
					<Command
						filter={(value, search) =>
							value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
						}
					>
						<CommandInput placeholder="Search users..." className="h-9" />
						<CommandList>
							<CommandEmpty>No user found.</CommandEmpty>
							<CommandItem key="clear" value="cl3@r" onSelect={handleSelect}>
								{'Remove Volunteer'}
							</CommandItem>
							{users.map((user) => (
								<CommandItem
									key={user.quickbooks}
									value={user.quickbooks}
									onSelect={handleSelect}
									disabled={false}
								>
									{user.quickbooks}
									<Icon
										name="check"
										className={cn(
											'ml-auto',
											volunteerId === user.id ? 'opacity-100' : 'opacity-0',
										)}
									/>
								</CommandItem>
							))}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		)
	}

	return (
		<div
			id="user-row"
			className={`flex w-full max-w-256 flex-row items-start justify-between rounded-lg p-2 md:flex-row md:items-center ${borderColor({ acknowledged, volunteer })} ${background({ isCurrentSchedule, isCurrentUser, first })}`}
		>
			{showButtons.sorting && (
				<div
					id="sorting-buttons"
					className="mr-2 mb-2 flex h-full w-20 flex-col justify-center border-r-2"
				>
					<CrossoverSortingButtons crossoverId={crossoverId} order={order} />
				</div>
			)}
			<div id="column-wrapper" className="flex w-full flex-col">
				{(showButtons.admin || (volunteer && !acknowledged)) && (
					<div
						id="volunteer-row"
						className="border-secondary-foreground flex w-full flex-row items-center justify-between border-b-2 pb-2"
					>
						<strong
							id="volunteer-name"
							className="mb-1 overflow-hidden text-nowrap text-ellipsis"
						>
							<div
								className={`${isCurrentSchedule ? 'text-white' : 'text-blue-700'}`}
							>
								{'[VOLUNTEER]: '}
								{showButtons.admin ? (
									<VolunteerDropdownMenu
										users={users}
										crossoverId={crossoverId}
										scheduleDate={schedule.date}
										currentVolunteer={volunteerId}
									/>
								) : (
									volunteer
								)}
							</div>
						</strong>
					</div>
				)}
				<div
					id="user-details"
					className="flex h-full w-full flex-col justify-between p-2 pb-0 md:flex-row md:items-center"
				>
					<div
						id="quickbooks-duty"
						className="flex w-full flex-col items-start justify-between"
					>
						<div
							id="name-and-trained"
							className="flex flex-col gap-1.5 sm:flex-row"
						>
							<strong
								id="quickbooks"
								className={`mb-1 overflow-hidden text-nowrap text-ellipsis underline ${acknowledged ? 'text-green-700 decoration-green-700' : acknowledged === false ? 'text-red-700 decoration-red-700' : ''}`}
							>
								{quickbooks ? `${ditchPosition}: ${quickbooks}` : null}
							</strong>
							<div id="badges" className="flex flex-row items-center gap-1.5">
								{showButtons.admin && (
									<DutiesDropdownMenu
										crossoverId={crossoverId}
										display={display ?? ''}
									/>
								)}
								{userId && (
									<Badge
										id="acknowledged"
										className={`h-8 ${first || isCurrentSchedule || isCurrentUser ? 'bg-background' : 'bg-muted/40'} ${foregroundColor({ acknowledged })} capitalize`}
										variant="outline"
									>
										<Icon
											name={acknowledgedBadgeIcon(acknowledged)}
											className={`h-6 w-6 py-0.5 ${foregroundColor({ acknowledged })}`}
											aria-hidden="true"
										/>
										<div className="text-nowrap text-ellipsis">
											{acknowledgedBadgeText(acknowledged)}
										</div>
									</Badge>
								)}
								{requestsTraining && (
									<Badge
										id="trained"
										className={`h-8 ${backgroundColor('default')} ${foregroundColor({ requestsTraining })} capitalize`}
										variant="outline"
									>
										<Icon
											name="diploma"
											className={`h-6 w-6 pt-1 pr-1 ${foregroundColor({ requestsTraining })}`}
											aria-hidden="true"
										/>
										<div>Requests Training</div>
									</Badge>
								)}
							</div>
						</div>
						<div
							id="badges-and-duty"
							className="flex h-full w-full flex-row items-center gap-1 pb-2"
						>
							<Badge
								id="first-badge"
								className={`hidden sm:flex ${first ? 'ml-[40px]' : 'ml-[10px]'} mr-1 h-6 text-white capitalize ${backgroundColor(duty)} ${isCurrentSchedule && 'text-white'}`}
								variant="outline"
							>
								{duty}
							</Badge>
							<div id="column-wrapper" className="flex w-full flex-col">
								<div>{dutyDetail}</div>
								{showButtons.admin ? (
									<Input
										id="dutyNotes"
										className="w-full"
										defaultValue={currentDutyNotes ?? ''}
										onBlur={(e) => handleChange(e.currentTarget.value)}
									/>
								) : (
									<div>{currentDutyNotes}</div>
								)}
							</div>
						</div>
					</div>
					{currentUser && (
						<div id="buttons-row">
							<Form
								method="POST"
								{...getFormProps(form)}
								className="flex flex-col gap-1 align-middle"
							>
								<input type="hidden" name="crossoverId" value={crossoverId} />
								<input type="hidden" name="volunteerId" value={currentUser} />
								{isCurrentUser && !acknowledged && (
									<Button
										type="submit"
										name="intent"
										value="acknowledge"
										variant="outline"
										disabled={isLocked}
										className="text-primary w-36 border-2 border-green-700 shadow-sm shadow-gray-700"
									>
										Acknowledge
									</Button>
								)}
								{isCurrentUser && !requestsTraining && (
									<Button
										type="submit"
										name="intent"
										value="training"
										variant="outline"
										disabled={isLocked}
										className="text-primary w-36 border-2 border-[#f3ab23] shadow-sm shadow-gray-700"
									>
										Request Training
									</Button>
								)}
								{isCurrentUser && acknowledged !== false && (
									<Button
										type="submit"
										name="intent"
										value="assistance"
										variant="outline"
										disabled={isLocked}
										className="text-primary w-36 border-2 border-red-700 shadow-sm shadow-gray-700"
									>
										Request Help
									</Button>
								)}
								{!isCurrentUser &&
									volunteer === null &&
									acknowledged !== true && (
										<Button
											type="submit"
											name="intent"
											value="volunteer"
											variant="outline"
											disabled={isLocked}
											className="text-primary w-40 border-2 border-blue-700 shadow-sm shadow-gray-700"
										>
											Volunteer to Help
										</Button>
									)}
								{isCurrentVolunteer && (
									<Button
										type="submit"
										name="intent"
										value="unvolunteer"
										variant="outline"
										disabled={isLocked}
										className="text-primary w-40 border-2 border-red-700 shadow-sm shadow-gray-700"
									>
										Remove Volunteer
									</Button>
								)}
							</Form>
						</div>
					)}

					<div
						id="distance-start"
						className="border-secondary-foreground m-2 flex h-full min-w-60 flex-col items-start justify-center border-t pl-2 md:mt-0 md:border-t-0"
					>
						{startString ? (
							<>
								<div
									id="distance-to-now"
									className="text-body-sm min-w-60 overflow-hidden pr-2 text-ellipsis"
								>
									{distanceToNow}
								</div>
								<div
									id="duty-start"
									className="text-body-sm min-w-44 overflow-hidden text-ellipsis"
								>
									{dutyStartString}
								</div>
								<div
									id="start"
									className="text-body-xs min-w-44 overflow-hidden text-ellipsis"
								>
									&nbsp;{`[${startString}]`}
								</div>
							</>
						) : showButtons.sorting ? (
							<input
								className="bg-secondary text-body-sm float-right mt-2 w-full rounded-sm border border-black p-2"
								aria-label="Date and time"
								type="datetime-local"
								step="300"
								value={dutyStartDate.toISOString().slice(0, 16)}
								onChange={handleChangeTimestamp}
							/>
						) : (
							<>
								<div
									id="distance-to-now"
									className="text-body-sm min-w-60 overflow-hidden pr-2 text-ellipsis"
								>
									{distanceToNow}
								</div>
								<div
									id="duty-start"
									className="text-body-sm min-w-44 overflow-hidden text-ellipsis"
								>
									{dutyStartString}
								</div>
							</>
						)}
					</div>
					{showButtons.sorting && (
						<div
							id="column-wrapper"
							className="flex h-full w-20 flex-col justify-end border-l-2 pl-1"
						>
							<CrossoverSortingButtons
								crossoverId={crossoverId}
								start={dutyStart}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

/**
 * CSS classes and styles
 *  - referenceKey
 *  - foregroundColor
 *  - borderColor
 *  - background
 */
const referenceKey = [
	{
		color: 'green-700',
		label: 'Acknowledged',
		description:
			'Use these buttons to confirm that you will complete the duty.',
		className: 'mt-1',
	},
	{
		color: '[#f3ab23]',
		label: 'Request Training',
		description:
			'Use these buttons if you would like to attempt the duty but need guidance or support.',
	},
	{
		color: 'red-700',
		label: 'Request Help',
		description:
			'Use these buttons if you do not wish to make the duty yourself and would like a volunteer to handle it.',
	},
	{
		color: 'blue-700',
		label: 'Volunteer to Help',
		description: 'Use these buttons to volunteer to handle the duty.',
		className: 'mb-0.5',
	},
	{
		color: 'yellow-700',
		label: 'No Response',
		description: `Default status for all users until they Acknowledge or Request Help.\nShould the designated member Acknowledge after a member has volunteered, the volunteer will be removed.`,
		className: 'mb-2',
	},
]
const foregroundColor = ({
	trained,
	requestsTraining,
	acknowledged,
}: {
	trained?: boolean | null
	requestsTraining?: boolean | null
	acknowledged?: boolean | null
}) => {
	if (trained) return 'text-blue-700 border-blue-700'
	if (requestsTraining) return 'text-[#f3ab23] border-[#f3ab23]'
	if (acknowledged) return 'text-green-700 border-green-700'
	if (acknowledged === false) return 'text-red-700 border-red-700'
	return 'text-yellow-700 border-yellow-700'
}
const borderColor = ({
	acknowledged,
	volunteer,
}: {
	acknowledged?: boolean | null
	volunteer?: string | null
}) => {
	if (acknowledged) return 'border-2 border-green-700'
	if (volunteer) return 'border-2 border-blue-700'
	if (acknowledged === false) return 'border-2 border-red-700'
	return 'border border-secondary-foreground'
}
const background = ({
	isCurrentSchedule,
	isCurrentUser,
	first,
}: {
	isCurrentSchedule: boolean
	isCurrentUser: boolean
	first?: boolean | null
}) => {
	if (isCurrentSchedule) return 'bg-sky-800 text-white'
	if (isCurrentUser) return 'bg-secondary'
	if (first) return 'bg-secondary/20'
	return ''
}

export const meta: Route.MetaFunction = () => {
	return [
		{ title: 'Irrigation | Gate Changes & Crossovers' },
		{
			name: 'description',
			content: `Clearwater Farms 1 Irrigation Timeline`,
		},
	]
}

function acknowledgedBadgeIcon(
	acknowledged: boolean | null | undefined,
): 'check-circled' | 'cross-circled' | 'exclamation-triangle' {
	switch (acknowledged) {
		case true:
			return 'check-circled'
		case false:
			return 'cross-circled'
		default:
			return 'exclamation-triangle'
	}
}

function acknowledgedBadgeText(
	acknowledged: boolean | null | undefined,
): 'Acknowledged' | 'Requests Help' | 'No Response' {
	switch (acknowledged) {
		case true:
			return 'Acknowledged'
		case false:
			return 'Requests Help'
		default:
			return 'No Response'
	}
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
